import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import React, { useState, useEffect } from 'react';
import { createContext, useContext, useMemo } from 'react';

// Context for state that changes (offset, isVisible)
const StickyNavBarOffsetDataContext = createContext<
  Readonly<{
    offset: number;
    isVisible: boolean;
  }>
>({
  offset: 0,
  isVisible: true,
});

// Context for functions that never change (hide, show)
const StickyNavBarOffsetApiContext = createContext<
  Readonly<{
    hide: () => void;
    show: () => void;
  }>
>({
  hide: () => {},
  show: () => {},
});

interface StickyNavBarOffsetProviderProps
  extends Readonly<{
    offset: number;
  }> {}

export const StickyNavBarOffsetProvider: React.FC<StickyNavBarOffsetProviderProps> = ({ children, offset }) => {
  const [isVisible, setIsVisible] = useState(true);

  const hide = useEventCallback(() => {
    setIsVisible(false);
  });

  const show = useEventCallback(() => {
    setIsVisible(true);
  });

  // State that changes - memoized to only update when dependencies change
  const data = useMemo(() => ({ offset, isVisible }), [offset, isVisible]);

  // Functions that never change - memoized with stable references from useEventCallback
  const api = useMemo(() => ({ hide, show }), [hide, show]);

  return (
    <StickyNavBarOffsetDataContext.Provider value={data}>
      <StickyNavBarOffsetApiContext.Provider value={api}>{children}</StickyNavBarOffsetApiContext.Provider>
    </StickyNavBarOffsetDataContext.Provider>
  );
};

/**
 * Hook to access sticky navigation bar state data (offset, isVisible).
 * Components using this hook will re-render when offset or isVisible changes.
 *
 * @requires StickyNavBarOffsetProvider must be present in the component tree
 *
 * @returns {object} State data containing:
 * - offset: Banner offset height for the navbar
 * - isVisible: Current visibility state of the navbar
 */
export const useStickyNavBarOffsetData = () => {
  return useContext(StickyNavBarOffsetDataContext);
};

/**
 * Hook to access sticky navigation bar API functions (hide, show).
 * Components using this hook will NOT re-render when offset or isVisible changes.
 * This is optimized for components that only need to control visibility without reading state.
 *
 * @requires StickyNavBarOffsetProvider must be present in the component tree
 *
 * @returns {object} API functions containing:
 * - hide: Function to programmatically hide the navbar
 * - show: Function to programmatically show the navbar
 *
 * @example
 * ```tsx
 * // Prefer useRegisterOverStickyNav for automatic lifecycle management
 * const { hide, show } = useStickyNavBarOffsetApi();
 * ```
 */
export const useStickyNavBarOffsetApi = () => {
  return useContext(StickyNavBarOffsetApiContext);
};

/**
 * Legacy hook that provides both state and API.
 * For better performance, prefer using useStickyNavBarOffsetData or useStickyNavBarOffsetApi directly.
 *
 * @deprecated Use useStickyNavBarOffsetData or useStickyNavBarOffsetApi for better performance
 */
export const useStickyNavBarOffset = () => {
  const data = useStickyNavBarOffsetData();
  const api = useStickyNavBarOffsetApi();
  return { ...data, ...api };
};

/**
 * Declaratively registers a component as rendering over the sticky navigation bar.
 * When shouldHideNav is true, the navbar is hidden. When the component unmounts or
 * shouldHideNav becomes false, the navbar is automatically restored.
 *
 * This hook follows the pattern: hide on mount (when true), show on unmount.
 *
 * Performance: This hook uses only the API context, so components won't re-render
 * when offset or isVisible changes - only when shouldHideNav changes.
 *
 * @requires StickyNavBarOffsetProvider must be present in the component tree
 *
 * @param shouldHideNav - Whether this component should hide the sticky navbar
 *
 * @example
 * ```tsx
 * const AccommodationsMap = () => {
 *   const [isFullScreen, setIsFullScreen] = useState(false);
 *
 *   // Navbar automatically hides when fullscreen, restores when not fullscreen or on unmount
 *   // Component won't re-render when navbar offset or visibility changes
 *   useRegisterOverStickyNav(isFullScreen);
 *
 *   return <DirectionsMap>...</DirectionsMap>;
 * };
 * ```
 */
export const useRegisterOverStickyNav = (shouldHideNav: boolean) => {
  // Only use API context - won't re-render when data (offset, isVisible) changes
  const { hide, show } = useStickyNavBarOffsetApi();

  useEffect(() => {
    if (shouldHideNav) {
      hide();
    } else {
      show();
    }

    // Cleanup: always restore navbar visibility on unmount
    return () => {
      show();
    };
  }, [shouldHideNav, hide, show]);
};
